import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { filtersSelector } from 'store/modules/competition-nav/selectors/filters-selector';
import {
    useGetVenuesQuery,
    useGetMatchesQuery
} from 'store/modules/api/afl-api';
import { filterByCity } from '../../../../../sites/aflm/fixtures/js/utils/filter-by-city';
import { filterByTeam } from '../../../../../sites/aflm/fixtures/js/utils/filter-by-team';
import { getTranslation } from '../../../utils/translations';
import { SelectMultiple } from '../../select-multiple';

import { setFilterItem } from 'common/store/modules/competition-nav/init';

export const VenueFilterItem = ({
    addOnResetCallback,
    currentFilters,
    maximumSelections,
    counterThreshold
}) => {
    const dispatch = useDispatch();

    const { competitionId, compSeason } = currentFilters;
    const filters = useSelector(filtersSelector);

    const { data: venuesList } = useGetVenuesQuery(compSeason.id, {
        skip: !compSeason.id ? true : false
    });

    const venues = useMemo(
        () =>
            venuesList
                ? [...venuesList].sort((a, b) => a.name.localeCompare(b.name))
                : null,
        [venuesList]
    );

    const updateFilterItem = (filterItem, value) => {
        dispatch(
            setFilterItem({
                filterItem,
                value
            })
        );
    };

    const { data: matches = [] } = useGetMatchesQuery(
        {
            pageSize: 300,
            competitionId: competitionId,
            compSeasonId: compSeason.id,
            ...(filters.teams.length && { teamId: filters.teams }),
            ...(filters.venues.length && { venueId: filters.venues })
        },
        {
            skip: !competitionId || !compSeason.id
        }
    );

    const cityFilteredMatches = filters.cities.length
        ? filterByCity(matches, filters.cities)
        : null;

    const teamFilteredMatches = filters.teams.length
        ? filterByTeam(matches, filters.teams)
        : null;
    // if team or city selected
    // disable non-applicable venues

    let activeVenues = [];

    if (filters.teams.length || filters.cities.length) {
        if (cityFilteredMatches?.length) {
            cityFilteredMatches.forEach((match) => {
                if (!activeVenues.includes(match.venue?.id)) {
                    activeVenues = [...activeVenues, match.venue?.id];
                }
            });
        } else if (teamFilteredMatches?.length) {
            teamFilteredMatches.forEach((match) => {
                if (!activeVenues.includes(match.venue?.id)) {
                    activeVenues = [...activeVenues, match.venue?.id];
                }
            });
        } else if (matches.length) {
            matches.forEach((match) => {
                activeVenues = [...activeVenues, match.venue?.id];
            });
        }
    }

    return (
        <SelectMultiple
            type="with-limit"
            maximumSelections={maximumSelections}
            counterThreshold={counterThreshold}
            items={venues}
            selections={venues?.filter(({ id }) =>
                filters?.venues?.map(Number).includes(id)
            )}
            getInitialSelectedItems={() => {
                return venues.filter(({ id }) =>
                    filters.venues.map(Number).includes(id)
                );
            }}
            label={getTranslation('label.fixtures.filters.venue')}
            title={getTranslation('label.fixtures.filters.venues')}
            icon="venue"
            itemToString={(venue) => venue.name}
            onSelectedItemsChange={(venue) => {
                updateFilterItem(
                    'venues',
                    venue.map(({ id }) => id)
                );
            }}
            addOnResetCallback={addOnResetCallback}
            getItemText={(item) => item.name}
            getItemLabel={(item) => (
                <span
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                        lineHeight: 1,
                        width: '100%'
                    }}
                >
                    <span style={{ marginRight: '1rem' }}>{item.name}</span>
                </span>
            )}
            getItemDisabled={(item) =>
                activeVenues.length && !activeVenues.includes(item.id)
            }
            maximumSelectionsLabel={PULSE.I18N.lookup(
                'label.filters.venues.maxSelected',
                { maximumSelections }
            )}
        />
    );
};

VenueFilterItem.propTypes = {
    addOnResetCallback: PropTypes.func,
    currentFilters: PropTypes.object,
    maximumSelections: PropTypes.number,
    counterThreshold: PropTypes.number
};
